/**
 * Удаление блока в конструкторе — с отменой (28.09.2026, разбор удобства админки №6).
 *
 * ⚠️ ЧТО БЫЛО. Корзинка на блоке удаляла сразу, без вопроса: id уходил в очередь удалений, и через 2,5 с
 * автосохранение стирало блок на сервере. Промах мышью по соседней иконке — и задание пропадало безвозвратно.
 *
 * Теперь: подтверждение, затем плашка «Блок удалён · Отменить» на `UNDO_MS`. Пока плашка видна, блок убран только
 * с экрана — в очередь удалений его id не попадает, и автосохранение его не трогает. «Отменить» возвращает блок на
 * прежнее место; срок вышел (или удалили следующий блок, или уходят со страницы) — удаление уходит в очередь.
 */
export const UNDO_MS = 8000;

type Block = { id: string; serverId?: string };
type Section<B extends Block> = { id: string; blocks: B[] };

/** Убранный с экрана, но ещё не удалённый блок: где стоял и что это. */
export type PendingRemoval<B extends Block> = { sectionId: string; index: number; block: B };

/** Убрать блок из раздела; `removed` — что вернуть по «Отменить» (null — блока нет). */
export function takeBlock<B extends Block, S extends Section<B>>(
  sections: readonly S[],
  sectionId: string,
  blockId: string,
): { sections: S[]; removed: PendingRemoval<B> | null } {
  let removed: PendingRemoval<B> | null = null;
  const next = sections.map((sec) => {
    if (sec.id !== sectionId) return sec;
    const index = sec.blocks.findIndex((b) => b.id === blockId);
    if (index < 0) return sec;
    removed = { sectionId, index, block: sec.blocks[index] };
    return { ...sec, blocks: sec.blocks.filter((b) => b.id !== blockId) };
  });
  return { sections: next, removed };
}

/**
 * Вернуть блок на прежнее место. Раздел за это время удалили — вернуть некуда, разделы как были. Прежнее место
 * дальше конца (соседей тоже убрали) — в конец.
 */
export function putBack<B extends Block, S extends Section<B>>(sections: readonly S[], r: PendingRemoval<B>): S[] {
  return sections.map((sec) => {
    if (sec.id !== r.sectionId || sec.blocks.some((b) => b.id === r.block.id)) return sec;
    const at = Math.min(Math.max(r.index, 0), sec.blocks.length);
    return { ...sec, blocks: [...sec.blocks.slice(0, at), r.block, ...sec.blocks.slice(at)] };
  });
}

/** Что уходит в очередь удалений на сервере, когда отмена больше невозможна. Несохранённый блок — ничего. */
export function removalServerIds<B extends Block>(r: PendingRemoval<B> | null): string[] {
  return r?.block.serverId ? [r.block.serverId] : [];
}
